/**
 * WYSIWYG text field with inline formatting (bold, italic, underline, and the
 * Danone highlighter).
 *
 * Formatting is shown as formatting, never as markers: the user sees bold
 * text, not `**bold**`. Underneath, the value stays a plain string carrying
 * markers (see lib/richText), which keeps Cosmos, the CSV exports and the
 * phase-2 AI input free of HTML.
 *
 * Implemented on contentEditable rather than a rich-text library. Three
 * details make that safe to do:
 *
 *  - The DOM is only written from `toHtml`, whose text runs are escaped, so
 *    nothing untrusted reaches innerHTML.
 *  - Writes back into the element are suppressed while the user is typing
 *    (`lastEmitted`), because re-rendering a contentEditable from React
 *    destroys the caret position mid-word.
 *  - Paste is intercepted and inserted as plain text, so pasting from Word or
 *    Outlook cannot smuggle in fonts, colours or tags we do not model.
 *
 * The toolbar either sits above the field (`toolbar="always"`, for the
 * narrative box) or appears on focus (`toolbar="focus"`, for bullet rows,
 * where a dozen permanent toolbars would swamp the list).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Bold, Italic, Underline, Highlighter, List } from 'lucide-react'
import { fromDom, toHtml, HIGHLIGHT_CSS, type MarkKind } from '../lib/richText'

const BUTTONS: { kind: MarkKind; icon: typeof Bold; label: string; hint: string }[] = [
  { kind: 'bold', icon: Bold, label: 'Bold', hint: '⌘B' },
  { kind: 'italic', icon: Italic, label: 'Italic', hint: '⌘I' },
  { kind: 'underline', icon: Underline, label: 'Underline', hint: '⌘U' },
  { kind: 'highlight', icon: Highlighter, label: 'Highlight', hint: '⌘⇧H' },
]

const EXEC: Partial<Record<MarkKind, string>> = {
  bold: 'bold', italic: 'italic', underline: 'underline',
}

// Bullets are a block construct, not an inline mark, so they are handled apart
// from MarkKind and only offered where line breaks are allowed.
const BULLET_CMD = 'insertUnorderedList'

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Allows line breaks. Single-line fields swallow Enter instead. */
  multiline?: boolean
  /** Visual height in rows, multiline only. */
  rows?: number
  className?: string
  readOnly?: boolean
  toolbar?: 'always' | 'focus' | 'none'
}

export default function RichTextInput({
  value, onChange, placeholder, multiline = false, rows = 8,
  className = '', readOnly = false, toolbar = 'focus',
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  // The last value this component emitted. Guards the sync effect below so a
  // parent re-render caused by our own onChange does not rewrite the DOM
  // under the caret.
  const lastEmitted = useRef<string | null>(null)
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState<Record<string, boolean>>({})

  // Write the incoming value in only when it differs from what we last sent
  // up, i.e. on mount, on month switch, or on a prefill.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (value === lastEmitted.current) return
    el.innerHTML = toHtml(value)
    lastEmitted.current = value
  }, [value])

  const emit = useCallback(() => {
    const el = ref.current
    if (!el) return
    const next = fromDom(el)
    lastEmitted.current = next
    onChange(next)
  }, [onChange])

  /** Is the caret inside a <mark>? Returns the element so it can be unwrapped. */
  const enclosingMark = (): HTMLElement | null => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || !ref.current) return null
    let node: Node | null = sel.anchorNode
    while (node && node !== ref.current) {
      if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'MARK') {
        return node as HTMLElement
      }
      node = node.parentNode
    }
    return null
  }

  const refreshActive = useCallback(() => {
    if (!ref.current || document.activeElement !== ref.current) return
    setActive({
      bullets: document.queryCommandState(BULLET_CMD),
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      highlight: !!enclosingMark(),
    })
  }, [])

  const toggleHighlight = () => {
    const existing = enclosingMark()
    if (existing) {
      // Unwrap: lift the children out, then drop the empty <mark>.
      const parent = existing.parentNode
      if (!parent) return
      while (existing.firstChild) parent.insertBefore(existing.firstChild, existing)
      parent.removeChild(existing)
      return
    }
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return
    const range = sel.getRangeAt(0)
    const mark = document.createElement('mark')
    Object.assign(mark.style, HIGHLIGHT_CSS)
    try {
      range.surroundContents(mark)
    } catch {
      // surroundContents throws when the selection straddles element
      // boundaries (half in a <strong>, half out). Extracting and re-inserting
      // handles that case.
      mark.appendChild(range.extractContents())
      range.insertNode(mark)
    }
    sel.removeAllRanges()
  }

  const apply = (kind: MarkKind) => {
    if (readOnly) return
    ref.current?.focus()
    const cmd = EXEC[kind]
    if (cmd) document.execCommand(cmd)
    else toggleHighlight()
    emit()
    refreshActive()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === 'Enter') {
      // Single-line fields must not grow a second line; the slide lays these
      // out as one line and a stray break would silently clip.
      e.preventDefault()
      return
    }
    if (!(e.metaKey || e.ctrlKey)) return
    const key = e.key.toLowerCase()
    if (e.shiftKey && key === 'h') { e.preventDefault(); apply('highlight'); return }
    const kind = ({ b: 'bold', i: 'italic', u: 'underline' } as const)[key as 'b' | 'i' | 'u']
    if (!kind) return
    e.preventDefault()
    apply(kind)
  }

  const onPaste = (e: React.ClipboardEvent) => {
    // Take the text and drop the formatting: pasting from Word or Outlook
    // otherwise brings fonts, sizes and colours that would break the slide's
    // type scale and cannot round-trip through the marker format.
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, multiline ? text : text.replace(/\s*\n+\s*/g, ' '))
  }

  useEffect(() => {
    // Tag-based output (<b>) rather than styled spans, so what we read back
    // matches what fromDom expects. Harmless if the browser ignores it.
    try { document.execCommand('styleWithCSS', false, 'false') } catch { /* not supported */ }
  }, [])

  const toggleBullets = () => {
    if (readOnly) return
    ref.current?.focus()
    document.execCommand(BULLET_CMD)
    emit()
    refreshActive()
  }

  const showToolbar = !readOnly && (toolbar === 'always' || (toolbar === 'focus' && focused))

  return (
    <div className="relative">
      {showToolbar && (
        <div className={`flex items-center gap-1 ${
          toolbar === 'always'
            ? 'mb-1.5'
            : 'absolute -top-8 right-0 z-10 bg-white border border-slate-200 rounded-lg shadow-sm px-1 py-0.5'
        }`}>
          {BUTTONS.map(({ kind, icon: Icon, label, hint }) => (
            <button
              key={kind}
              type="button"
              // Keep focus (and the selection) on the field; without this the
              // button steals focus and there is nothing left to format.
              onMouseDown={e => e.preventDefault()}
              onClick={() => apply(kind)}
              title={`${label} (${hint})`}
              aria-label={label}
              aria-pressed={!!active[kind]}
              className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                active[kind]
                  ? 'bg-[#005EB8]/10 text-[#005EB8]'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
          {/* Bullets are a block construct, so they sit apart from the inline
              marks and only appear where line breaks are allowed. */}
          {multiline && (
            <>
              <span className="w-px h-4 bg-slate-200 mx-0.5" />
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={toggleBullets}
                title="Bulleted list"
                aria-label="Bulleted list"
                aria-pressed={!!active.bullets}
                className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                  active.bullets
                    ? 'bg-[#005EB8]/10 text-[#005EB8]'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      )}
      <div
        ref={ref}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline={multiline}
        aria-label={placeholder}
        data-placeholder={placeholder}
        onInput={emit}
        onKeyDown={onKeyDown}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        onPaste={onPaste}
        onFocus={() => { setFocused(true); refreshActive() }}
        // Delayed so a mousedown on a toolbar button does not hide the toolbar
        // before its click lands.
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        className={`${className} rich-editable ${multiline ? 'overflow-y-auto whitespace-pre-wrap' : 'whitespace-nowrap overflow-x-auto'}`}
        style={multiline ? { minHeight: `${rows * 1.5}rem` } : undefined}
      />
    </div>
  )
}
